import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface QBOConnectionStatus {
  connected: false;
  configured: boolean;
}

export interface QBOConnectionStatusConnected {
  connected: true;
  configured: true;
  companyName: string;
  realmId: string;
  connectedAt: string;
  needsReconnect: boolean;
}

export type QBOStatus = QBOConnectionStatus | QBOConnectionStatusConnected;

export interface QBOSyncLogEntry {
  id: number;
  userId: number;
  source: string;
  jobId: number | null;
  entityType: string;
  entityId: number | null;
  direction: string;
  status: string;
  payloadSummary: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  startedAt: string;
  finishedAt: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const QBO_STATUS_QUERY_KEY = ["quickbooks", "status"] as const;
export const QBO_SYNC_LOG_QUERY_KEY = ["quickbooks", "syncLog"] as const;
export const qboInvoiceSyncStatusQueryKey = (invoiceId: number) =>
  ["quickbooks", "invoiceSyncStatus", invoiceId] as const;
export const qboEstimateSyncStatusQueryKey = (estimateId: number) =>
  ["quickbooks", "estimateSyncStatus", estimateId] as const;
export const QBO_BULK_SYNC_STATUS_QUERY_KEY = ["quickbooks", "bulkSyncStatus"] as const;
export const qboBulkSyncStatusQueryKey = (
  entityType: "invoice" | "estimate",
  ids: number[],
) =>
  [...QBO_BULK_SYNC_STATUS_QUERY_KEY, entityType, [...ids].sort((a, b) => a - b)] as const;

// ---------------------------------------------------------------------------
// Functions
// ---------------------------------------------------------------------------

export async function getQBOStatus(): Promise<QBOStatus> {
  return customFetch<QBOStatus>("/api/integrations/quickbooks/status");
}

export async function disconnectQBO(): Promise<{ ok: boolean }> {
  return customFetch<{ ok: boolean }>("/api/integrations/quickbooks/disconnect", { method: "POST" });
}

export async function getQBOSyncLog(limit = 50): Promise<{ entries: QBOSyncLogEntry[] }> {
  return customFetch<{ entries: QBOSyncLogEntry[] }>(`/api/integrations/quickbooks/sync-log?limit=${limit}`);
}

export async function pushInvoiceToQBO(invoiceId: number): Promise<{ ok: boolean; qboId: string }> {
  return customFetch<{ ok: boolean; qboId: string }>(`/api/invoices/${invoiceId}/push-to-quickbooks`, {
    method: "POST",
  });
}

export async function pushEstimateToQBO(estimateId: number): Promise<{ ok: boolean; qboId: string }> {
  return customFetch<{ ok: boolean; qboId: string }>(`/api/integrations/quickbooks/push-estimate/${estimateId}`, {
    method: "POST",
  });
}

export interface QBOSyncStatusResponse {
  entry: QBOSyncLogEntry | null;
  lastSuccess: QBOSyncLogEntry | null;
  externalId: string | null;
}

export async function getQBOInvoiceSyncStatus(invoiceId: number): Promise<QBOSyncStatusResponse> {
  return customFetch<QBOSyncStatusResponse>(`/api/integrations/quickbooks/invoice-sync-status/${invoiceId}`);
}

export async function getQBOEstimateSyncStatus(estimateId: number): Promise<QBOSyncStatusResponse> {
  return customFetch<QBOSyncStatusResponse>(`/api/integrations/quickbooks/estimate-sync-status/${estimateId}`);
}

export interface QBOBulkSyncStatusEntry {
  externalId: string | null;
  lastSuccessAt: string | null;
  lastFailedAt: string | null;
}

export interface QBOBulkSyncStatusResponse {
  statuses: Record<string, QBOBulkSyncStatusEntry>;
}

export async function getQBOBulkSyncStatus(
  entityType: "invoice" | "estimate",
  ids: number[],
): Promise<QBOBulkSyncStatusResponse> {
  return customFetch<QBOBulkSyncStatusResponse>(
    "/api/integrations/quickbooks/bulk-sync-status",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entityType, ids }),
    },
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useGetQBOStatus() {
  return useQuery({
    queryKey: QBO_STATUS_QUERY_KEY,
    queryFn: getQBOStatus,
    staleTime: 30_000,
  });
}

export function useDisconnectQBO() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectQBO,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QBO_STATUS_QUERY_KEY });
    },
  });
}

export function useGetQBOSyncLog(limit = 50) {
  return useQuery({
    queryKey: [...QBO_SYNC_LOG_QUERY_KEY, limit],
    queryFn: () => getQBOSyncLog(limit),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function usePushInvoiceToQBO() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pushInvoiceToQBO,
    onSuccess: (_data, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: QBO_SYNC_LOG_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: qboInvoiceSyncStatusQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: QBO_BULK_SYNC_STATUS_QUERY_KEY });
    },
    onError: (_err, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: qboInvoiceSyncStatusQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: QBO_BULK_SYNC_STATUS_QUERY_KEY });
    },
  });
}

export function usePushEstimateToQBO() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pushEstimateToQBO,
    onSuccess: (_data, estimateId) => {
      queryClient.invalidateQueries({ queryKey: QBO_SYNC_LOG_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: qboEstimateSyncStatusQueryKey(estimateId) });
      queryClient.invalidateQueries({ queryKey: QBO_BULK_SYNC_STATUS_QUERY_KEY });
    },
    onError: (_err, estimateId) => {
      queryClient.invalidateQueries({ queryKey: qboEstimateSyncStatusQueryKey(estimateId) });
      queryClient.invalidateQueries({ queryKey: QBO_BULK_SYNC_STATUS_QUERY_KEY });
    },
  });
}

export function useGetQBOBulkSyncStatus(
  entityType: "invoice" | "estimate",
  ids: number[],
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: qboBulkSyncStatusQueryKey(entityType, ids),
    queryFn: () => getQBOBulkSyncStatus(entityType, ids),
    enabled: options?.enabled !== false && ids.length > 0,
    staleTime: 10_000,
  });
}

export function useGetQBOInvoiceSyncStatus(invoiceId: number | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qboInvoiceSyncStatusQueryKey(invoiceId ?? 0),
    queryFn: () => getQBOInvoiceSyncStatus(invoiceId!),
    enabled: options?.enabled !== false && invoiceId != null,
    staleTime: 10_000,
  });
}

export function useGetQBOEstimateSyncStatus(estimateId: number | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qboEstimateSyncStatusQueryKey(estimateId ?? 0),
    queryFn: () => getQBOEstimateSyncStatus(estimateId!),
    enabled: options?.enabled !== false && estimateId != null,
    staleTime: 10_000,
  });
}
